import { Icon } from "@chakra-ui/react";
import { useCallback, useMemo } from "react";
import { LuClock, LuListChecks, LuTriangleAlert } from "react-icons/lu";
import FilterButton from "components/Button/FilterButton";
import FilterChipMenu from "components/Button/FilterChipMenu";
import FilterSortSearchBar from "components/ControlBar/FilterSortSearchBar";
import { ALL_STAGES_MINUS_IMMEDIATE_DEPENDENCY } from "features/Stage/constants";

export interface NewHireJourneysFilters {
  q?: string;
  needs_attention?: string;
  with_open_tasks?: string;
  with_overdue_tasks?: string;
  stage?: string;
}

// The three task-status chips below behave as a radio set — selecting one
// clears the others. Driving them off a single key keeps the toolbar honest
// (a journey can't simultaneously be in three buckets) and matches the
// product's mental model: pick one lens at a time.
type TaskStatusFilterKey = "needs_attention" | "with_overdue_tasks" | "with_open_tasks";
const TASK_STATUS_FILTER_KEYS: TaskStatusFilterKey[] = [
  "needs_attention",
  "with_overdue_tasks",
  "with_open_tasks",
];

interface NewHireJourneysFiltersProps {
  filters: NewHireJourneysFilters;
  onFiltersChange: (filters: NewHireJourneysFilters) => void;
  onReset: () => void;
  currentSort: string;
  defaultSort: string;
  onSortChange: (value: string) => void;
  sortOptions: Array<{ label: string; value: string }>;
}

const stageOptions = ALL_STAGES_MINUS_IMMEDIATE_DEPENDENCY.map((stage) => ({
  label: stage.label,
  value: stage.key,
}));

export default function NewHireJourneysFiltersComponent({
  filters,
  onFiltersChange,
  onReset,
  currentSort,
  defaultSort,
  onSortChange,
  sortOptions,
}: NewHireJourneysFiltersProps) {
  const handleFilterChange = useCallback((key: keyof NewHireJourneysFilters, value: string | undefined) => {
    const newValue = value === "" ? undefined : value;
    if (filters[key] !== newValue) {
      onFiltersChange({
        ...filters,
        [key]: newValue,
      });
    }
  }, [filters, onFiltersChange]);

  const hasActiveFilters = useMemo(() => Object.entries(filters).some(
    ([key, value]) => key !== "q" && value !== undefined && value !== "",
  ), [filters]);

  // Mutually-exclusive selection: turning one task-status chip on clears the
  // other two in the same call so the URL/query stays at most one filter deep.
  const handleTaskStatusToggle = useCallback((key: TaskStatusFilterKey) => {
    const isActive = filters[key] === "true";
    const next = { ...filters };
    TASK_STATUS_FILTER_KEYS.forEach((k) => { next[k] = undefined; });
    if (!isActive) next[key] = "true";
    onFiltersChange(next);
  }, [filters, onFiltersChange]);

  return (
    <FilterSortSearchBar
      searchPlaceholder="Search new hires..."
      searchValue={filters.q}
      onSearchChange={(value) => handleFilterChange("q", value)}
      currentSort={currentSort}
      defaultSort={defaultSort}
      onSortChange={onSortChange}
      sortOptions={sortOptions}
      quickFilters={(
        <>
          <FilterButton
            onClick={() => handleTaskStatusToggle("needs_attention")}
            isActive={filters.needs_attention === "true"}
            icon={<Icon as={LuTriangleAlert} boxSize={4} />}
            size="sm"
          >
            Needs Attention
          </FilterButton>
          <FilterButton
            onClick={() => handleTaskStatusToggle("with_overdue_tasks")}
            isActive={filters.with_overdue_tasks === "true"}
            icon={<Icon as={LuClock} boxSize={4} />}
            size="sm"
          >
            With Overdue Tasks
          </FilterButton>
          <FilterButton
            onClick={() => handleTaskStatusToggle("with_open_tasks")}
            isActive={filters.with_open_tasks === "true"}
            icon={<Icon as={LuListChecks} boxSize={4} />}
            size="sm"
          >
            With Open Tasks
          </FilterButton>
          <FilterChipMenu
            label="Stage"
            options={stageOptions}
            value={filters.stage}
            onChange={(next) => handleFilterChange("stage", next)}
          />
        </>
      )}
      activeFilters={[]}
      onReset={onReset}
      hasActiveFilters={hasActiveFilters}
    />
  );
}
